import { EventListener, HttpClient } from "silver";

type FormInputElement = HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement;
type FormValue = string|boolean;

const OBJ_REGEX = /^\[(.+?)]/;
const ARR_REGEX = /^\[]/;

export class FormState {

	public readonly onIsDirty = EventListener.create("isDirty");
	public readonly onDirtyChange = EventListener.create<{ isDirty: boolean }>("dirtyChange");
	public readonly onIsClean = EventListener.create("isClean");
	public readonly onChange = EventListener.create("change");

	private isCleanValue: boolean = true;

	constructor(
		private form: HTMLFormElement
	) {
		this.init();
	}

	public element(): HTMLElement {
		return this.form.element;
	}

	public isClean(): boolean {
		return this.isCleanValue;
	}

	public get action(): string {
		return this.form.action;
	}

	public get method(): "GET"|"POST" {
		// form.method only supports GET and POST
		return (this.form.getAttribute("method") || "get").toUpperCase() as "GET"|"POST"
	}

	private getInputs(): FormInputElement[] {
		return Array.from(this.form.querySelectorAll("input[name],select[name],textarea[name]"));
	}

	public export(exportEmpty: boolean = false): any {
		const inputs = this.getInputs().filter((input: FormInputElement) => input.hasAttribute("name"));
		const out: any = { };
		for (let i = 0; i < inputs.length; i++) {
			const input = inputs[ i ];
			const name = input.name;
			const type = input.type;
			let value: FormValue = "";

			if (!FormState.isCheckboxOrRadio(input) || input.checked) {
				value = input.value;
			}
			if (value || exportEmpty) {
				FormState.parseNameAndPlaceIntoObject(name, out, value, type != "radio");
			}
		}
		return out;
	}

	private static parseNameAndPlaceIntoObject(name: string, target: any, value: any, override: boolean = false): void {
		const firstBracketIndex = name.indexOf("[");
		let node = target;
		if (firstBracketIndex === -1) {
			if (override || ! (target.hasOwnProperty(name) && target[ name ])) {
				target[ name ] = value;
			}
			return;
		}

		let lastKey = name.slice(0, firstBracketIndex);
		name = name.slice(firstBracketIndex);
		let matches = name.match(OBJ_REGEX);

		while (matches) {
			if (! node.hasOwnProperty(lastKey)) {
				node[ lastKey ] = { };
			}
			node = node[ lastKey ];
			lastKey = matches[ 1 ];
			name = name.slice(matches[ 0 ].length);
			matches = name.match(OBJ_REGEX);
		}

		matches = name.match(ARR_REGEX);
		if (! matches && (override || ! (node.hasOwnProperty(lastKey) && node[ lastKey ]))) {
			node[ lastKey ] = value;
		}
		if (!matches) {
			return;
		}
		if (! node.hasOwnProperty(lastKey)) {
			node[ lastKey ] = [];
		}
		node[ lastKey ].push(value);
	}

	public markEmptyIsClean(): void {
		const inputs = this.getInputs();
		for (let i = 0; i < inputs.length; i++) {
			const input = inputs[ i ];
			if (FormState.isCheckboxOrRadio(input)) {
				const value = input.checked;
				input.defaultChecked = false;
				input.checked = value;
			} else if (FormState.isSelect(input)) {
				if (input.options.length > 0) {
					const value = input.selectedIndex;
					Array.from(input.options).forEach((option, j) => {
						option.defaultSelected = (j === 0);
					});
					input.selectedIndex = value;
				}
			} else {
				const value = input.value;
				input.defaultValue = "";
				input.value = value;
			}
		}
		this.doGroupChange();
	}

	public markClean() {
		if (this.isCleanValue) {
			return;
		}

		const inputs = this.getInputs();
		for (let i = 0; i < inputs.length; i++) {
			const input = inputs[ i ];
			if (FormState.isCheckboxOrRadio(input)) {
				input.defaultChecked = input.checked;
			} else if (FormState.isSelect(input)) {
				Array.from(input.options).forEach((option, j) => {
					option.defaultSelected = (j === input.selectedIndex);
				});
			} else {
				input.defaultValue = input.value;
			}
		}

		this.isCleanValue = true;
		this.onIsClean.trigger({ });
		this.onChange.trigger({ });
	}

	public listDirty(): any {
		const inputs = this.getInputs();
		const out: any = { };
		for (let i = 0; i < inputs.length; i++) {
			const input = inputs[ i ];
			if (input.getAttribute("data-dirty") != "dirty") {
				continue;
			}
			FormState.parseNameAndPlaceIntoObject(input.name, out, true);
		}
		return out;
	}

	public restore(): void {
		if (this.isCleanValue) {
			return;
		}
		this.form.reset();
		this.onIsClean.trigger({ });
		this.isCleanValue = true;
		this.doGroupChange();
	}

	public doGroupChange(): void {
		this.doChange(...this.getInputs());
	}

	public doChange(...elements: FormInputElement[]) {
		elements.forEach((element) => {
			if (FormState.hasName(element)) {
				this.atomicDoChange(element);
			}
		});
		this.onChange.trigger({ });
	}

	public async submitAsync(httpClient: HttpClient, extraData: object = { }): Promise<any> {
		if (this.form.getAttribute("disabled")) {
			return Promise.reject("Form is disabled");
		}
		this.form.setAttribute("disabled", "disabled");
		const data = { ...this.export(), ...extraData };
		const body = this.method == "GET" ? undefined : data;
		const query = this.method == "POST" ? data : undefined;

		const results = await httpClient.ajax(this.action || ".", {
			method: this.method,
			body,
			query
		});

		this.form.removeAttribute("disabled");
		return results;
	}

	private atomicDoChange(element: FormInputElement): void {
		const inputIsClean = FormState.isInputClean(element);
		if (FormState.isRadio(element)) {
			this.propagateRadioChange(element);
		}

		element.setAttribute("data-dirty", inputIsClean ? "" : "dirty");
		if (inputIsClean && this.isCleanValue) {
			return;
		}
		if (! inputIsClean) {
			this.setIsClean(false);
			return;
		}

		const formIsClean = this.form.querySelectorAll("[data-dirty=dirty]").length === 0;
		this.setIsClean(formIsClean);
	}

	private setIsClean(formIsClean: boolean): void {
		if (formIsClean == this.isCleanValue) {
			return;
		}
		this.isCleanValue = formIsClean;
		(formIsClean ? this.onIsClean : this.onIsDirty).trigger({ });
		this.onDirtyChange.trigger({ isDirty: !formIsClean });
	}

	private init(): void {
		this.form.addEventListener("change", ({ target }) => {
			if (FormState.isFormInput(target)) {
				this.doChange(target);
			}
		});
		this.form.addEventListener("keyup", ({ target }: KeyboardEvent) => {
			if (FormState.isFormInput(target)) {
				this.doChange(target);
			}
		});
	}

	private static isInputClean(element: FormInputElement): boolean {
		if (FormState.isCheckboxOrRadio(element)) {
			return element.checked == element.defaultChecked;
		}
		if (!FormState.isSelect(element)) {
			return element.defaultValue == element.value;
		}

		const options = Array.from(element.options);
		if (options.length === 0) {
			return true;
		}
		const hasNoDefaultOptions = options.find(o => o.defaultSelected) === undefined;
		if (hasNoDefaultOptions && !element.multiple) {
			return element.selectedIndex === 0;
		}

		return options.find(o => o.defaultSelected !== o.selected) === undefined;
	}

	private static isFormInput(target: any): target is FormInputElement {
		if (! (target instanceof HTMLElement)) {
			return false;
		}
		return ([ "SELECT", "INPUT", "TEXTAREA" ].includes(target.tagName.toUpperCase()));
	}

	public static isCheckboxOrRadio(target: any): target is HTMLInputElement {
		return FormState.isFormInput(target) && (target.type == "radio" || target.type == "checkbox");
	}

	public static isRadio(target: any): target is HTMLInputElement {
		return FormState.isFormInput(target) && (target.type == "radio");
	}

	public static isSelect(target: any): target is HTMLSelectElement {
		return FormState.isFormInput(target) && (target.tagName.toUpperCase() === "SELECT");
	}

	private static hasName(element: FormInputElement): boolean {
		return element.hasAttribute("name");
	}

	private propagateRadioChange(initial: HTMLInputElement): void {
		const name = initial.name;
		const inputs: NodeListOf<HTMLInputElement> = this.form.querySelectorAll(`input[name="${name}"]`);
		for (let i = 0; i < inputs.length; i++) {
			if (inputs[ i ] === initial) {
				continue;
			}
			inputs[ i ].setAttribute("data-dirty", FormState.isInputClean(inputs[ i ]) ? "" : "dirty");
		}
	}
}
